import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import adminAxios from "../../../api/adminAxios";

const InterestedUsers = () => {
  const { id } = useParams();

  const [interestedUsers, setInterestedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  // ✅ Validation helpers
  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidPhone = (phone) =>
    /^[0-9]{10}$/.test(phone); // adjust if needed

  useEffect(() => {
    let mounted = true;

    const fetchInterestedUsers = async () => {
      setLoading(true);
      setError("");

      try {
        const { data } = await adminAxios.get(
          `/api/admin/sellers/${id}/interested-users`
        );

        if (!mounted) return;

        const list = Array.isArray(data)
          ? data
          : data?.interestedUsers || [];

        setInterestedUsers(list);
      } catch (err) {
        if (!mounted) return;

        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to fetch interested users";

        setError(msg);
        setInterestedUsers([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id) fetchInterestedUsers();
    else {
      setLoading(false);
      setError("Seller ID missing");
    }

    return () => {
      mounted = false;
    };
  }, [id]);

  // ✅ Filter + Strict Validation
  const filteredUsers = useMemo(() => {
    return interestedUsers.filter((item) => {
      const user = item?.userId || {};
      const product = item?.productId || {};

      // ✅ Remove invalid users completely
      if (
        !user?.name ||
        !isValidEmail(user?.email) ||
        !isValidPhone(user?.phone) ||
        !product?.name
      ) {
        return false;
      }

      const searchText = search.toLowerCase();

      return (
        user.name.toLowerCase().includes(searchText) ||
        user.email.toLowerCase().includes(searchText) ||
        user.phone.toLowerCase().includes(searchText) ||
        product.name.toLowerCase().includes(searchText)
      );
    });
  }, [search, interestedUsers]);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3">
        <h2 className="text-xl font-bold">
          Interested Users ({filteredUsers.length})
        </h2>

        {/* Search */}
        <input
          type="text"
          placeholder="Search by name, email, phone, product..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border px-3 py-2 rounded-md w-full md:w-80"
        />
      </div>

      {/* Loading */}
      {loading && (
        <p className="text-gray-600 animate-pulse">Loading...</p>
      )}

      {/* Error */}
      {error && <p className="text-red-600">{error}</p>}

      {/* Empty */}
      {!loading && !error && filteredUsers.length === 0 && (
        <p className="text-gray-500">
          No valid interested users found.
        </p>
      )}

      {/* Table */}
      {!loading && !error && filteredUsers.length > 0 && (
        <div className="overflow-x-auto bg-white shadow rounded-lg">
          <table className="min-w-full border text-sm">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="p-3 border">#</th>
                <th className="p-3 border">User Name</th>
                <th className="p-3 border">Email</th>
                <th className="p-3 border">Phone</th>
                <th className="p-3 border">City</th>
                <th className="p-3 border">Product</th>
                <th className="p-3 border">Date</th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.map((item, index) => {
                const user = item?.userId;
                const product = item?.productId;

                return (
                  <tr
                    key={item?._id || index}
                    className="hover:bg-gray-50 transition"
                  >
                    <td className="p-3 border">{index + 1}</td>

                    <td className="p-3 border font-medium">
                      {user.name}
                    </td>

                    <td className="p-3 border">
                      {user.email}
                    </td>

                    <td className="p-3 border">
                      {user.phone}
                    </td>

                    <td className="p-3 border">
                      {user?.city || "-"}
                    </td>

                    <td className="p-3 border font-medium">
                      {product.name}
                    </td>

                    <td className="p-3 border text-gray-600">
                      {item?.createdAt
                        ? new Date(item.createdAt).toLocaleString()
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default InterestedUsers;